/**
 * Transform tab tooltip definitions - optimized structure to reduce duplication
 */

// Common signature for all JavaScript/Python transforms
const STANDARD_SIGNATURE = '(valueObject, keyObject, topic, timestamp, commonObject, valueSchema, keySchema)';

// Base transform definitions with unique content
const TRANSFORM_DEFINITIONS = {
    // JavaScript/Python transforms
    COMMON_TRANSFORM: ['Common Transform', 'Prepare shared variables and calculations for other transform functions', 'Shared computation results'],
    VALUE_TRANSFORM: ['Value Transform', 'Modify record payload content and structure', 'Modified record payload'],
    KEY_TRANSFORM: ['Key Transform', 'Alter record identifier and partitioning logic', 'Updated record identifier'],
    TOPIC_TRANSFORM: ['Topic Transform', 'Route records to different destination streams', 'Target stream identifier'],
    VALUE_SCHEMA_TRANSFORM: ['Value Schema Transform', 'Specify payload structure and field constraints', 'Payload structure specification'],
    KEY_SCHEMA_TRANSFORM: ['Key Schema Transform', 'Configure identifier format and validation rules', 'Identifier format specification'],

    // SQL-specific transforms
    CREATE_TABLE_MAIN: [
        'Main Table Schema',
        'Primary stream structure for enrichment operations',
        'Primary stream DDL definition',
        'CREATE TABLE main_table (...)'
    ],
    CREATE_TABLE_LOOKUP: [
        'Lookup Table Schema',
        'Reference data structure for enrichment joins',
        'Reference data DDL definition',
        'CREATE TABLE lookup_table (...)'
    ],
    ENRICH_SQL: [
        'Enrich SQL',
        'Augment primary stream with reference data',
        'Augmented stream with additional fields',
        'SELECT ... FROM main_table JOIN lookup_table ...'
    ],
    JOIN_SQL: [
        'Join SQL',
        'Combine multiple input sources into unified output',
        'Unified dataset from multiple sources',
        'SELECT ... FROM table_1 JOIN table_2 ON ...'
    ]
};

// Dynamic table definitions (CREATE_TABLE_1 through CREATE_TABLE_5)
const TABLE_NAMES = ['Primary', 'Secondary', 'Tertiary', 'Quaternary', 'Quinary'];

// Generate tooltips dynamically to avoid duplication
export const TRANSFORM_TOOLTIPS = Object.fromEntries([
    // Standard transforms
    ...Object.entries(TRANSFORM_DEFINITIONS).map(([key, [title, description, returns, customSignature]]) => {
        const signature = customSignature || `_streamkap_transform_${key.toLowerCase().replace('_transform', '')}${STANDARD_SIGNATURE}`;
        return [key, { title, description, signature, returns }];
    }),

    // Dynamic table definitions (CREATE_TABLE_1 through CREATE_TABLE_5)
    ...Array.from({ length: 5 }, (_, i) => {
        const tableNum = i + 1;
        return [
            `CREATE_TABLE_${tableNum}`,
            {
                title: `Table ${tableNum} Schema`,
                description: `${TABLE_NAMES[i]} input source structure`,
                signature: `CREATE TABLE table_${tableNum} (...)`,
                returns: `${TABLE_NAMES[i]} source DDL`
            }
        ];
    })
]);

/**
 * Get tooltip details for a transform type with fallback
 */
export function getTransformTooltip(transformType, fallbackLabel = 'Transform') {
    return (
        TRANSFORM_TOOLTIPS[transformType] || {
            title: fallbackLabel,
            description: 'Custom transformation logic',
            signature: 'function()',
            returns: 'Processed output'
        }
    );
}
